import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI, ratingAPI } from '../../services/api';
import CRMLayout from './CRMLayout';

const SITE = 'https://advokattop.uz';

const TABS = [
  { key: '',          label: 'Барчаси' },
  { key: 'submitted', label: 'Юборилган' },
  { key: 'reviewed',  label: 'Кўриб чиқилган' },
  { key: 'draft',     label: 'Қоралама' },
];

const statusStyle = (s) => {
  if (s === 'submitted') return { bg: '#fefcbf', color: '#b7791f', label: 'Юборилган' };
  if (s === 'reviewed')  return { bg: '#c6f6d5', color: '#276749', label: 'Кўриб чиқилган' };
  return { bg: '#f1f5f9', color: '#718096', label: 'Қоралама' };
};

const btn = (bg, size) => ({
  backgroundColor: bg, color: 'white', border: 'none',
  padding: size === 'small' ? '0.4rem 0.9rem' : '0.5rem 1.1rem',
  borderRadius: '8px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
});

const LANG_LABELS = {
  english: 'Инглиз', russian: 'Рус', french: 'Француз', german: 'Немис', spanish: 'Испан',
  arabic: 'Араб', chinese: 'Хитой', korean: 'Корейс', japanese: 'Япон', turkish: 'Турк', other: 'Бошқа',
};
const ACTIVITY_TYPE_LABELS = { book: 'Китоб', article: 'Илмий мақола', conference: 'Конференция', advocacy: 'Ҳуқуқий тарғибот' };

// Mirrors RatingSubmission.jsx's caps and per-criterion point rules.
const PRACTICE_MAX = 15;
const CERTIFICATE_MAX = 3;
const LANGUAGE_MAX = 2;
const LEVEL_POINTS = { A1: 0.5, A2: 0.5, B1: 0.5, B2: 1, C1: 1, C2: 1 };
// Pass/fail — at least one certificate earns the full CERTIFICATE_MAX, not counted per document.
const certificatePoints = (count) => (count > 0 ? CERTIFICATE_MAX : 0);
const languageRowPoints = (level) => LEVEL_POINTS[level] || 0;
const languageTotalPoints = (entries) => Math.min((entries || []).reduce((sum, l) => sum + languageRowPoints(l.level), 0), LANGUAGE_MAX);

// 1 point per full year of practice, plus the current year's months prorated out of 10
// (10 and 11 months both give the full extra point).
const practicePoints = (years, months) => {
  const monthsCapped = Math.min(Number(months) || 0, 10);
  const points = (Number(years) || 0) + monthsCapped / 10;
  return Math.min(Math.round(points * 10) / 10, PRACTICE_MAX);
};

const INVESTIGATION_DOC_POINTS = 0.5;
const INVESTIGATION_MAX = 4;
const COURT_CATEGORY_MAX = 4;
const CONTRACT_MAX = 5;
const COURT_CATEGORIES = [
  { field: 'court_criminal_orders', label: 'Жиноят' },
  { field: 'court_civil_orders', label: 'Фуқаролик' },
  { field: 'court_administrative_orders', label: 'Маъмурий' },
  { field: 'court_economic_orders', label: 'Иқтисодий' },
];
const investigationPoints = (count) => Math.min((count || 0) * INVESTIGATION_DOC_POINTS, INVESTIGATION_MAX);
const courtCategoryPoints = (count) => Math.min((count || 0) * 0.5, COURT_CATEGORY_MAX);
const courtTotalPoints = (detail) => COURT_CATEGORIES.reduce((sum, c) => sum + courtCategoryPoints((detail?.[c.field] || []).length), 0);
const contractPoints = (count) => Math.min(count || 0, CONTRACT_MAX);

const SATISFACTION_MAX = 20;
const satisfactionPoints = (average, count) => Math.min(Math.round(((average * count) / 5) * 10) / 10, SATISFACTION_MAX);

const LICENSE_SCOPE_POINTS = 1.5;
const OFFICE_INFO_ITEM_POINTS = 0.5;
const OFFICE_HOURS_POINTS = 1;
const ARTICLE_MAX = 2;
const ARTICLES_PER_POINT = 5;
const SOCIAL_ITEM_POINTS = 0.5;
const PHONE_FORMAT = /^\+998 \d{2} \d{3} \d{2} \d{2}$/;
const TELEGRAM_FORMAT = /^https:\/\/t\.me\/.+/;

const licenseScopePoints = (fileUrl) => (fileUrl ? LICENSE_SCOPE_POINTS : 0);
const officeInfoPoints = (detail) => [
  !!detail.office_address,
  !!detail.office_location_link,
  (detail.office_photos || []).length > 0,
  PHONE_FORMAT.test(detail.office_phone || ''),
].reduce((sum, filled) => sum + (filled ? OFFICE_INFO_ITEM_POINTS : 0), 0);
const officeHoursPoints = (detail) => (detail.office_hours_days && detail.office_hours_start && detail.office_hours_end ? OFFICE_HOURS_POINTS : 0);
const articlePoints = (count) => Math.min(Math.round((count / ARTICLES_PER_POINT) * 10) / 10, ARTICLE_MAX);
const socialPoints = (detail) => [
  !!detail.social_website,
  TELEGRAM_FORMAT.test(detail.social_telegram || ''),
  !!detail.social_instagram,
  !!detail.social_youtube,
].reduce((sum, filled) => sum + (filled ? SOCIAL_ITEM_POINTS : 0), 0);

const OFFICE_HOURS_DAYS_LABELS = { mon_fri: 'Душанба – Жума', mon_sat: 'Душанба – Шанба', every_day: 'Ҳар куни' };

const FileLink = ({ url, label }) => url ? (
  <a href={`${SITE}${url}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontSize: '0.85rem' }}>
    {label || 'Файлни очиш'}
  </a>
) : <span style={{ color: '#a0aec0', fontSize: '0.85rem' }}>—</span>;

const DetailSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{title}</div>
    <div style={{ fontSize: '0.9rem', color: '#2d3748' }}>{children}</div>
  </div>
);

const Row = ({ label, children }) => (
  <div style={{ display: 'flex', gap: '0.75rem', padding: '0.4rem 0', borderBottom: '1px solid #f0f4f8' }}>
    <div style={{ minWidth: '220px', color: '#718096', fontSize: '0.85rem' }}>{label}</div>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const CRMRatings = () => {
  const [activeTab, setActiveTab] = useState('submitted');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [satisfaction, setSatisfaction] = useState({ average: 0, count: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await crmAPI.getRatingSubmissions(activeTab || undefined);
      setRows(res.data);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    if (detailId === id) { setDetailId(null); setDetail(null); return; }
    setDetailId(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await crmAPI.getRatingSubmissionDetail(id);
      setDetail(res.data);
      setSatisfaction({ average: 0, count: 0 });
      if (res.data.attorney_id) {
        ratingAPI.get(res.data.attorney_id)
          .then(r => setSatisfaction({ average: r.data.average || 0, count: r.data.count || 0 }))
          .catch(() => {});
      }
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const markReviewed = async (id) => {
    setActingId(id);
    try {
      await crmAPI.markRatingSubmissionReviewed(id);
      await load();
      if (detailId === id) openDetail(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  const reopen = async (id) => {
    setActingId(id);
    try {
      await crmAPI.reopenRatingSubmission(id);
      await load();
      if (detailId === id) openDetail(id);
    } catch (err) {
      alert(err.response?.data?.error || 'Хатолик');
    } finally {
      setActingId(null);
    }
  };

  return (
    <CRMLayout>
      <div style={{ padding: '2rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1a365d', margin: '0 0 1.5rem' }}>
          Рейтинг аризалари
        </h1>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '0.5rem 1.1rem', border: 'none', cursor: 'pointer', fontSize: '0.88rem',
                fontWeight: activeTab === tab.key ? '700' : '400',
                color: activeTab === tab.key ? '#1a365d' : '#718096',
                backgroundColor: 'transparent',
                borderBottom: activeTab === tab.key ? '2px solid #1a365d' : '2px solid transparent',
                marginBottom: '-2px', fontFamily: 'inherit',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Юкланмоқда...</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#718096', textAlign: 'center', padding: '3rem' }}>Аризалар йўқ</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {rows.map(r => {
              const st = statusStyle(r.status);
              const dateStr = r.submitted_at || r.updated_at;
              return (
                <div key={r.id} style={{
                  backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
                  padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
                        <span style={{ backgroundColor: st.bg, color: st.color, fontSize: '0.75rem', fontWeight: '600', padding: '0.2rem 0.65rem', borderRadius: '20px' }}>
                          {st.label}
                        </span>
                        <span style={{ fontSize: '0.78rem', color: '#a0aec0' }}>
                          #{r.id} · {dateStr ? new Date(dateStr).toLocaleDateString('uz-UZ') : ''}
                        </span>
                      </div>
                      <div style={{ fontWeight: '600', color: '#2d3748', marginBottom: '0.2rem' }}>{r.full_name}</div>
                      <div style={{ color: '#718096', fontSize: '0.85rem' }}>
                        {r.email} {r.bar_number ? `· Гувоҳнома № ${r.bar_number}` : ''}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
                      <button onClick={() => openDetail(r.id)} style={btn('#718096', 'small')}>
                        {detailId === r.id ? 'Ёпиш' : 'Кўриш'}
                      </button>
                      {r.status === 'submitted' && (
                        <button onClick={() => markReviewed(r.id)} disabled={actingId === r.id} style={btn(actingId === r.id ? '#a0aec0' : '#38a169', 'small')}>
                          Кўриб чиқилди деб белгилаш
                        </button>
                      )}
                      {r.status === 'reviewed' && (
                        <button onClick={() => reopen(r.id)} disabled={actingId === r.id} style={btn(actingId === r.id ? '#a0aec0' : '#e53e3e', 'small')}>
                          Қайта очиш
                        </button>
                      )}
                    </div>
                  </div>

                  {detailId === r.id && (
                    <div style={{ marginTop: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                      {detailLoading ? (
                        <div style={{ color: '#718096', textAlign: 'center', padding: '2rem' }}>Юкланмоқда...</div>
                      ) : !detail ? (
                        <div style={{ color: '#c53030', textAlign: 'center', padding: '1rem' }}>Юклашда хатолик</div>
                      ) : (
                        <>
                          <DetailSection title="Касбий малака">
                            <Row label="Адвокатлик стажи">
                              {(detail.years_of_practice_years || detail.years_of_practice_months)
                                ? <>{detail.years_of_practice_years || 0} йил {detail.years_of_practice_months || 0} ой
                                    {' '}(Балл: {practicePoints(detail.years_of_practice_years, detail.years_of_practice_months)})
                                    {' '}<FileLink url={detail.years_of_practice_file_url} label="(файл)" /></>
                                : '—'}
                            </Row>
                            <Row label={`Малака ошириш сертификати (Балл: ${certificatePoints((detail.training_courses || []).length)})`}>
                              {(detail.training_courses || []).length === 0 ? '—' : (detail.training_courses || []).map((c, i) => (
                                <div key={i} style={{ marginBottom: '0.3rem' }}>
                                  {c.originalName || c.url || 'Файл'} {c.url && <FileLink url={c.url} label="(файл)" />}
                                </div>
                              ))}
                            </Row>
                            <Row label={`Чет тиллари (Балл: ${languageTotalPoints(detail.foreign_language_entries)})`}>
                              {(detail.foreign_language_entries || []).length === 0 ? '—' : (detail.foreign_language_entries || []).map((l, i) => (
                                <div key={i} style={{ marginBottom: '0.3rem' }}>
                                  {l.language === 'other' ? (l.customLanguage || 'Бошқа') : (LANG_LABELS[l.language] || l.language)}
                                  {l.level ? ` — ${l.level} (+${languageRowPoints(l.level)})` : ''} {l.fileUrl && <FileLink url={l.fileUrl} label="(файл)" />}
                                </div>
                              ))}
                            </Row>
                          </DetailSection>

                          <DetailSection title="Амалий тажриба">
                            <Row label={`Терговда иштирок этганлик (Балл: ${investigationPoints((detail.investigation_participation_files || []).length)})`}>
                              {(detail.investigation_participation_files || []).length === 0 ? '—' : (detail.investigation_participation_files || []).map((f, i) => (
                                <div key={i} style={{ marginBottom: '0.3rem' }}>
                                  {f.originalName || f.url || 'Файл'} {f.url && <FileLink url={f.url} label="(файл)" />}
                                </div>
                              ))}
                            </Row>
                            <Row label={`Судларда иштирок этганлик (Балл: ${courtTotalPoints(detail)})`}>
                              {COURT_CATEGORIES.map(cat => (
                                <div key={cat.field} style={{ marginBottom: '0.3rem' }}>
                                  {cat.label} — {(detail[cat.field] || []).length} та ордер (+{courtCategoryPoints((detail[cat.field] || []).length)}):
                                  {' '}{(detail[cat.field] || []).map((f, i) => (
                                    <FileLink key={i} url={f.url} label={f.originalName || `(${i + 1})`} />
                                  ))}
                                  {(detail[cat.field] || []).length === 0 && '—'}
                                </div>
                              ))}
                            </Row>
                            <Row label={`Юридик шахсларга обуна асосида хизмат (Балл: ${contractPoints((detail.legal_entity_contracts || []).length)})`}>
                              {(detail.legal_entity_contracts || []).length === 0 ? '—' : (detail.legal_entity_contracts || []).map((f, i) => (
                                <div key={i}><FileLink url={f.url} label={f.originalName} /></div>
                              ))}
                            </Row>
                          </DetailSection>

                          <DetailSection title="Мижозлар қониқиши">
                            <Row label={`Балл: ${satisfactionPoints(satisfaction.average, satisfaction.count)}`}>
                              {satisfaction.average} ★ ({satisfaction.count} та шарҳ)
                            </Row>
                          </DetailSection>

                          <DetailSection title="Касбий обрў">
                            <Row label="Интизомий жазолар мавжуд эмас">{detail.no_disciplinary_sanctions ? 'Ҳа' : 'Йўқ'}</Row>
                            <Row label="Тасдиқловчи ҳужжат"><FileLink url={detail.no_disciplinary_sanctions_file} /></Row>
                            <Row label="Касбий этикага риоя">{detail.ethics_compliance ? 'Ҳа' : 'Йўқ'}</Row>
                            <Row label="Ҳамкасблар тавсиялари">
                              {(detail.peer_references || []).length === 0 ? '—' : (detail.peer_references || []).map((f, i) => (
                                <div key={i}><FileLink url={f.url} label={f.originalName} /></div>
                              ))}
                            </Row>
                            <Row label="Тавсиянома хатлар">
                              {(detail.recommendation_letters || []).length === 0 ? '—' : (detail.recommendation_letters || []).map((f, i) => (
                                <div key={i}><FileLink url={f.url} label={f.originalName} /></div>
                              ))}
                            </Row>
                          </DetailSection>

                          <DetailSection title="Очиқлик ва рақамли фаоллик">
                            <Row label={`Лицензия (Балл: ${licenseScopePoints(detail.license_econ_civil_file) + licenseScopePoints(detail.license_admin_criminal_file)})`}>
                              <div style={{ marginBottom: '0.3rem' }}>
                                Иқтисодий ва фуқаролик (+{licenseScopePoints(detail.license_econ_civil_file)}): <FileLink url={detail.license_econ_civil_file} label="(файл)" />
                              </div>
                              <div>
                                Маъмурий ва жиноий (+{licenseScopePoints(detail.license_admin_criminal_file)}): <FileLink url={detail.license_admin_criminal_file} label="(файл)" />
                              </div>
                            </Row>
                            <Row label={`Офис маълумотлари (Балл: ${officeInfoPoints(detail)})`}>
                              <div>Манзил: {detail.office_address || '—'}</div>
                              <div>
                                Локация: {detail.office_location_link
                                  ? <a href={detail.office_location_link} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce' }}>Харитада кўриш</a>
                                  : '—'}
                              </div>
                              <div>
                                Расмлар: {(detail.office_photos || []).length === 0 ? '—' : (detail.office_photos || []).map((f, i) => (
                                  <FileLink key={i} url={f.url} label={f.originalName || `(${i + 1})`} />
                                ))}
                              </div>
                              <div>Телефон: {detail.office_phone || '—'}</div>
                            </Row>
                            <Row label={`Иш вақти (Балл: ${officeHoursPoints(detail)})`}>
                              {detail.office_hours_days
                                ? `${OFFICE_HOURS_DAYS_LABELS[detail.office_hours_days] || detail.office_hours_days}, ${detail.office_hours_start}:00–${detail.office_hours_end}:00`
                                : '—'}
                            </Row>
                            <Row label={`Мақола (Балл: ${articlePoints(detail.published_articles_count || 0)})`}>
                              {detail.published_articles_count || 0} та мақола
                            </Row>
                            <Row label={`Ижтимоий тармоқларда фаоллик (Балл: ${socialPoints(detail)})`}>
                              <div>Сайт: {detail.social_website || '—'}</div>
                              <div>Телеграм: {detail.social_telegram || '—'}</div>
                              <div>Инстаграм: {detail.social_instagram || '—'}</div>
                              <div>Ютуб: {detail.social_youtube || '—'}</div>
                            </Row>
                          </DetailSection>

                          <DetailSection title="Илмий ва ижтимоий фаолият">
                            <Row label="Фаолият">
                              {(detail.scientific_activity || []).length === 0 ? '—' : (detail.scientific_activity || []).map((a, i) => (
                                <div key={i} style={{ marginBottom: '0.3rem' }}>
                                  {ACTIVITY_TYPE_LABELS[a.type] || a.type} — {a.title}
                                  {a.link && <> — <a href={a.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce' }}>{a.link}</a></>}
                                  {a.fileUrl && <> <FileLink url={a.fileUrl} label="(файл)" /></>}
                                </div>
                              ))}
                            </Row>
                          </DetailSection>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CRMLayout>
  );
};

export default CRMRatings;
